/**
 * TransitionManager - Handles transitions between video clips
 * 
 * Supports transitions that can be rendered both in Canvas preview
 * and replicated via FFmpeg xfade filter.
 */

export const TRANSITION_TYPES = {
  none: {
    name: 'None (Cut)',
    description: 'Hard cut between clips',
    ffmpegFilter: null,
  },
  fade: {
    name: 'Crossfade',
    description: 'Smooth fade between clips',
    ffmpegFilter: 'fade',
  },
  fadeblack: {
    name: 'Fade to Black',
    description: 'Fade out to black, then fade in',
    ffmpegFilter: 'fadeblack',
  },
  fadewhite: {
    name: 'Fade to White',
    description: 'Fade out to white, then fade in',
    ffmpegFilter: 'fadewhite',
  },
  slideLeft: {
    name: 'Slide Left',
    description: 'New clip slides in from right',
    ffmpegFilter: 'slideleft',
  },
  slideRight: {
    name: 'Slide Right',
    description: 'New clip slides in from left',
    ffmpegFilter: 'slideright',
  },
  slideUp: {
    name: 'Slide Up',
    description: 'New clip slides in from bottom',
    ffmpegFilter: 'slideup',
  },
  slideDown: {
    name: 'Slide Down',
    description: 'New clip slides in from top',
    ffmpegFilter: 'slidedown',
  },
  wipeLeft: {
    name: 'Wipe Left',
    description: 'Wipe reveal from right to left',
    ffmpegFilter: 'wipeleft',
  },
  wipeRight: {
    name: 'Wipe Right',
    description: 'Wipe reveal from left to right',
    ffmpegFilter: 'wiperight',
  },
  wipeUp: {
    name: 'Wipe Up',
    description: 'Wipe reveal from bottom to top',
    ffmpegFilter: 'wipeup',
  },
  wipeDown: {
    name: 'Wipe Down',
    description: 'Wipe reveal from top to bottom',
    ffmpegFilter: 'wipedown',
  },
  circleOpen: {
    name: 'Circle Open',
    description: 'Circular reveal expanding outward',
    ffmpegFilter: 'circleopen',
  },
  circleClose: {
    name: 'Circle Close',
    description: 'Circular reveal contracting inward',
    ffmpegFilter: 'circleclose',
  },
  dissolve: {
    name: 'Dissolve',
    description: 'Random pixel dissolve',
    ffmpegFilter: 'dissolve',
  },
  pixelize: {
    name: 'Pixelize',
    description: 'Pixelation transition',
    ffmpegFilter: 'pixelize',
  },
  radial: {
    name: 'Radial',
    description: 'Radial wipe',
    ffmpegFilter: 'radial',
  },
  smoothLeft: {
    name: 'Smooth Left',
    description: 'Smooth slide with blur effect',
    ffmpegFilter: 'smoothleft',
  },
  smoothRight: {
    name: 'Smooth Right', 
    description: 'Smooth slide with blur effect',
    ffmpegFilter: 'smoothright',
  },
};

export class TransitionManager {
  constructor(ctx, width, height) {
    this.ctx = ctx;
    this.width = width;
    this.height = height;
  }
  
  /**
   * Render transition between two frames
   * 
   * @param {string} type - Transition type
   * @param {ImageData|HTMLVideoElement|HTMLCanvasElement} fromFrame - Outgoing frame
   * @param {ImageData|HTMLVideoElement|HTMLCanvasElement} toFrame - Incoming frame
   * @param {number} progress - Transition progress (0-1)
   */
  render(type, fromFrame, toFrame, progress) {
    const method = this[`_render_${type}`];
    
    if (method) {
      method.call(this, fromFrame, toFrame, progress);
    } else {
      // Fallback to fade
      this._render_fade(fromFrame, toFrame, progress);
    }
  }
  
  /**
   * Draw a frame to canvas (handles different input types)
   */
  _drawFrame(frame, x = 0, y = 0, width = this.width, height = this.height) {
    if (!frame) return;
    
    if (frame instanceof ImageData) {
      this.ctx.putImageData(frame, x, y);
    } else {
      // Video element, canvas, or image
      this.ctx.drawImage(frame, x, y, width, height);
    }
  }
  
  /**
   * Crossfade transition
   */
  _render_fade(fromFrame, toFrame, progress) {
    // Draw from frame
    this.ctx.globalAlpha = 1 - progress;
    this._drawFrame(fromFrame);
    
    // Draw to frame
    this.ctx.globalAlpha = progress;
    this._drawFrame(toFrame);
    
    this.ctx.globalAlpha = 1;
  }
  
  /**
   * Fade to black transition
   */
  _render_fadeblack(fromFrame, toFrame, progress) {
    if (progress < 0.5) {
      // Fade out to black
      this._drawFrame(fromFrame);
      this.ctx.globalAlpha = progress * 2;
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.width, this.height);
    } else {
      // Fade in from black
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.width, this.height);
      this.ctx.globalAlpha = (progress - 0.5) * 2;
      this._drawFrame(toFrame);
    }
    this.ctx.globalAlpha = 1;
  }
  
  /**
   * Fade to white transition
   */
  _render_fadewhite(fromFrame, toFrame, progress) {
    if (progress < 0.5) {
      this._drawFrame(fromFrame);
      this.ctx.globalAlpha = progress * 2;
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.width, this.height);
    } else {
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.width, this.height);
      this.ctx.globalAlpha = (progress - 0.5) * 2;
      this._drawFrame(toFrame);
    }
    this.ctx.globalAlpha = 1;
  }
  
  /**
   * Slide left transition
   */
  _render_slideLeft(fromFrame, toFrame, progress) {
    const offset = this._easeInOutCubic(progress) * this.width;
    
    // From frame slides out to left
    this._drawFrame(fromFrame, -offset, 0);
    
    // To frame slides in from right
    this._drawFrame(toFrame, this.width - offset, 0);
  }
  
  /**
   * Slide right transition
   */
  _render_slideRight(fromFrame, toFrame, progress) {
    const offset = this._easeInOutCubic(progress) * this.width;
    
    // From frame slides out to right
    this._drawFrame(fromFrame, offset, 0);
    
    // To frame slides in from left
    this._drawFrame(toFrame, -this.width + offset, 0);
  }
  
  /**
   * Slide up transition
   */
  _render_slideUp(fromFrame, toFrame, progress) {
    const offset = this._easeInOutCubic(progress) * this.height;
    
    this._drawFrame(fromFrame, 0, -offset);
    this._drawFrame(toFrame, 0, this.height - offset);
  }
  
  /**
   * Slide down transition
   */
  _render_slideDown(fromFrame, toFrame, progress) {
    const offset = this._easeInOutCubic(progress) * this.height;
    
    this._drawFrame(fromFrame, 0, offset);
    this._drawFrame(toFrame, 0, -this.height + offset);
  }
  
  /**
   * Wipe left transition
   */
  _render_wipeLeft(fromFrame, toFrame, progress) {
    const wipeX = this.width * (1 - this._easeInOutCubic(progress));
    
    // Draw from frame
    this._drawFrame(fromFrame);
    
    // Draw to frame with clip
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(0, 0, this.width - wipeX, this.height);
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Wipe right transition
   */
  _render_wipeRight(fromFrame, toFrame, progress) {
    const wipeX = this.width * this._easeInOutCubic(progress);
    
    this._drawFrame(fromFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(wipeX, 0, this.width - wipeX, this.height);
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Wipe up transition
   */
  _render_wipeUp(fromFrame, toFrame, progress) {
    const wipeY = this.height * (1 - this._easeInOutCubic(progress));
    
    this._drawFrame(fromFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(0, 0, this.width, this.height - wipeY);
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Wipe down transition
   */
  _render_wipeDown(fromFrame, toFrame, progress) {
    const wipeY = this.height * this._easeInOutCubic(progress);
    
    this._drawFrame(fromFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(0, wipeY, this.width, this.height - wipeY);
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Circle open transition
   */
  _render_circleOpen(fromFrame, toFrame, progress) {
    const maxRadius = Math.sqrt(
      Math.pow(this.width / 2, 2) + Math.pow(this.height / 2, 2)
    );
    const radius = maxRadius * this._easeInOutCubic(progress);
    
    this._drawFrame(fromFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Circle close transition
   */
  _render_circleClose(fromFrame, toFrame, progress) {
    const maxRadius = Math.sqrt(
      Math.pow(this.width / 2, 2) + Math.pow(this.height / 2, 2)
    );
    const radius = maxRadius * (1 - this._easeInOutCubic(progress));
    
    this._drawFrame(toFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
    this.ctx.clip();
    this._drawFrame(fromFrame);
    this.ctx.restore();
  }
  
  /**
   * Dissolve transition (simplified version)
   */
  _render_dissolve(fromFrame, toFrame, progress) {
    // Use fade for dissolve as true dissolve requires pixel manipulation
    this._render_fade(fromFrame, toFrame, progress);
  }
  
  /**
   * Radial wipe transition
   */
  _render_radial(fromFrame, toFrame, progress) {
    const angle = Math.PI * 2 * this._easeInOutCubic(progress);
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    const maxRadius = Math.max(this.width, this.height);
    
    this._drawFrame(fromFrame);
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.moveTo(centerX, centerY);
    this.ctx.arc(centerX, centerY, maxRadius, -Math.PI / 2, -Math.PI / 2 + angle);
    this.ctx.closePath();
    this.ctx.clip();
    this._drawFrame(toFrame);
    this.ctx.restore();
  }
  
  /**
   * Easing function
   */
  _easeInOutCubic(t) {
    return t < 0.5 
      ? 4 * t * t * t 
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  
  /**
   * Get FFmpeg xfade filter string
   */
  static getFFmpegFilter(type, duration, offset) {
    const transition = TRANSITION_TYPES[type];
    if (!transition || !transition.ffmpegFilter) {
      return null;
    }
    
    return `xfade=transition=${transition.ffmpegFilter}:duration=${duration}:offset=${offset}`;
  }
  
  /**
   * Get list of available transitions
   */
  static getAvailableTransitions() {
    return Object.entries(TRANSITION_TYPES).map(([key, value]) => ({
      id: key,
      ...value,
    }));
  }
}

export default TransitionManager;

